<template>
  <div class="h-dvh w-dvw safe-top safe-bottom safe-right safe-left">
    <Analytics v-if="!app.isDevelop" />
    <CookieBar />
    <!-- Gestione dell'animazione con transition-group -->
    <transition-group name="loading-sequence">
      <!-- Animazione di caricamento con il logo -->
      <div v-if="restaurantStore.isLoading" key="loading" class="loading-container">
        <RestaurantLogo class="h-[130px] w-[130px] pulse" v-if="restaurantStore.restaurant?.logo" />
        <icon v-else name="logo" alt="Logo xMenu" class="logo-animation h-28 w-28 pulse" />
      </div>

      <!-- Contenuto principale con animazione di comparsa dal basso -->
      <div key="content" class="content-container size-full animation">
        <RouterView v-slot="{ Component }">
          <transition @after-leave="onLeaveCancelled" :name="nextAnimation">
            <component :is="Component" />
          </transition>
        </RouterView>
      </div>
    </transition-group>
    <x-modal v-show="isOpen" />
  </div>
</template>
<script setup>
import { onMounted, inject, computed, onBeforeMount, onUnmounted, defineAsyncComponent } from "vue";

import { useHistory } from "@/stores/history";
import { useStoreRestaurant } from "@/stores/restaurant";
import { useStoreSeo } from "@/stores/seo";
import { useStoreTranslation } from "@/stores/translation";
import { useStoreOrder } from "@/stores/order";
import { useStoreCustomer } from "@/stores/customer";
import { useStoreAnalytics } from "@/stores/analytics";
import { Loader } from "@googlemaps/js-api-loader";
import { useStoreModal } from "@/stores/modal";
import { useRoute, useRouter } from "vue-router";
import { useHead } from '@unhead/vue';
import { useStoreApp } from "@/stores/app";
import { usePay } from "@/composables/pay";
import { RouterView } from "vue-router";
import { Analytics } from '@vercel/analytics/vue';

const Icon = defineAsyncComponent(() => import('@/components/Icon.vue'));
const XModal = defineAsyncComponent(() => import('@/components/Modal.vue'));
const RestaurantLogo = defineAsyncComponent(() => import('@/components/RestaurantLogo.vue'));
const CookieBar = defineAsyncComponent(() => import('@/components/CookieBar.vue'));

let restaurantStore = useStoreRestaurant();
let translationStore = useStoreTranslation();
let seoStore = useStoreSeo();
let historyStore = useHistory();
let orderStore = useStoreOrder();
let customerStore = useStoreCustomer();
let app = useStoreApp();
let analyticsStore = useStoreAnalytics();
let { loadGooglePayScript } = usePay();

let modal = useStoreModal();
let route = useRoute();
let router = useRouter();
let canOrderIntervalId = null;

const logger = inject("logger");
const LOGGER_TAG = "app";

useHead({
  title: () => seoStore.seo.title,
  description: () => seoStore.seo.description,
  meta: () => seoStore.seo.meta,
});

onBeforeMount(async () => {
  restaurantStore.isLoading = true;
  await restaurantStore.getInfo();

  if (!restaurantStore.restaurant) {
    restaurantStore.isLoading = false;
    router.push("/not-found");
    return;
  }

  await Promise.all([
    translationStore.getLang(),
    restaurantStore.loadCustomCSS(),
    customerStore.getCustomer(),
    restaurantStore.getPromotions(),
    restaurantStore.getMapDetails()
  ]);

  await orderStore.getDeliveryMethods();
  await orderStore.getOrder();

  orderStore.getCanOrder();
  customerStore.checkTracking();

  canOrderIntervalId = setInterval(() => {
    orderStore.getCanOrder();
    customerStore.checkTracking();
  }, 60 * 1000);


  loadGooglePayScript();

  analyticsStore.initAnalytics(restaurantStore.restaurant?.analytics);

  restaurantStore.isLoading = false;
});

function onLeaveCancelled() {
  historyStore.updateAnimation("");
}

onMounted(async () => {
  await router.isReady();
  let config = await app.getConfig();
  if (config.cordova) {
    await app.addCordovaScript();
    await app.initialize();
  }
});

onUnmounted(() => {
  clearInterval(canOrderIntervalId);
});

const isOpen = computed(() => modal.isOpen);
const nextAnimation = computed(() => {
  logger.info(LOGGER_TAG, "nextAnimation", historyStore.animation, historyStore.firstLoad);
  // se è la prima volta che entriamo in un ristorante non usare nessuna animazione
  if (historyStore.firstLoad) {
    logger.info(LOGGER_TAG, "first load no animation");
    historyStore.firstLoad = false;
    return "empty";
  }

  logger.info(LOGGER_TAG, "Load with animation");

  return historyStore.animation || route.meta.transition || "slide-right";
});

const restaurant = computed(() => restaurantStore.restaurant);
</script>
<style scoped>
@reference "tailwindcss/theme";

/* Impostazioni di base del contenitore per il logo */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #fff;
}

/* Logo che pulsa */
.pulse {
  animation: pulse 1.5s infinite ease-in-out;
}

/* Animazione di pulsazione */
@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }
}

/* Animazioni per il gruppo di transizione loading-sequence */

/* Logo entra */
.loading-sequence-enter-active,
.loading-sequence-leave-active {
  transition: all 0.8s ease;
}

.loading-sequence-enter {
  transform: translateY(-100px);
  opacity: 0;
}

.loading-sequence-enter-to {
  transform: translateY(0);
  opacity: 1;
}

/* Logo esce */
.loading-sequence-leave-to {
  transform: translateY(100vh);
  opacity: 0;
}

/* Contenuto che entra */
.loading-sequence-enter-active {
  transition: transform 0.8s ease-out, opacity 0.8s ease-out;
}

.loading-sequence-enter .content-container {
  opacity: 0;
  transform: translateY(100px);
}

.loading-sequence-enter-to .content-container {
  opacity: 1;
  transform: translateY(0);
}
</style>
